/*******************************************************************************
* Licensed Materials - Property of HCL 
* (c) Copyright HCL Technologies Ltd. 2020. All Rights Reserved. 

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/

import React, { useState } from "react";
import PropTypes from "prop-types";
import BackgroundPanel from "./BackgroundPanel";
import Text from "./Text";
import ScrollBar from "./ScrollBar";
import EventHandlersManager from "../../controllers/EventHandlersManager";

const TextArea = ({
    width,
    height,
    text,
    textSize,
    textSizeDecimal,
    linesPerPage = 20,
    font,
    aframeClasses,
    ...otherAttributes
}) => {
    const [percentage, setPercentage] = useState(0);
    const focused = React.useRef(false);
    const root = React.useRef();

    const offset = height / 50;
    const lineHeight = (height - 2 * offset) / linesPerPage;
    const textWidth = width * 0.95 - 4 * offset;
    textSizeDecimal = textSizeDecimal || 50 / height;
    const wrapcount = textWidth * textSizeDecimal;
    let textLinesLength;

    const events = {
        mouseenter: {
            element: root,
            isReactRef: true,
            callback: (e) => {
                focused.current = true;
            }
        },
        mouseleave: {
            element: root,
            isReactRef: true,
            callback: (e) => {
                focused.current = false;
            }
        },
        wheel: (e) => {
            if (!focused.current) return;
            setPercentage((old) => {
                if (e.deltaY > 0 && old < 1) return old + 0.05;
                else if (e.deltaY < 0 && old > 0)
                    return old - 0.05 > 0 ? old - 0.05 : 0;
                else return old;
            });
        }
    };

    const drawText = () => {
        const textLines = text.split("\n").reduce((p, c) => {
            let res = [];
            if (c.length > wrapcount) {
                while (c.length > wrapcount) {
                    res = [...res, c.slice(0, wrapcount)];
                    c = c.slice(wrapcount);
                }
            }
            res = [...res, c];
            return [...p, ...res];
        }, []);
        textLinesLength = textLines.length;
        const startIndex = Math.floor(
            percentage * (textLines.length - linesPerPage)
        );
        return textLines
            .slice(startIndex, startIndex + linesPerPage)
            .map((x, index) => {
                return (
                    <Text
                        key={index}
                        width={textWidth}
                        text={x}
                        size={textSize}
                        sizeDecimal={textSizeDecimal}
                        position={`0 ${-index * lineHeight} 0`}
                        font={font}
                    />
                );
            });
    };
    return (
        <BackgroundPanel
            class={aframeClasses}
            ref={root}
            width={width}
            height={height}
            opacity={1}
            backgroundColor="#262626"
            {...otherAttributes}
        >
            <BackgroundPanel
                width={width - offset - width * 0.05}
                height={height - offset}
                opacity={1}
                position={`${-width * 0.025} 0 ${height / 500}`}
            >
                <a-entity
                    position={`${(-width * 0.95) / 2 + 2 * offset} ${
                        height / 2 - 3 * offset
                    } 0`}
                >
                    {text && drawText()}
                </a-entity>
            </BackgroundPanel>
            <ScrollBar
                width={width * 0.05}
                height={height}
                position={`${width / 2 - width * 0.025} 0 ${height / 200}`}
                percentage={percentage}
                setPercentage={setPercentage}
                aframeClasses={aframeClasses}
                windowContentsRatio={
                    linesPerPage / (textLinesLength || linesPerPage)
                }
            />
            <EventHandlersManager events={events} enabled={true} />
        </BackgroundPanel>
    );
};

TextArea.propTypes = {
    width: PropTypes.PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    height: PropTypes.PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired
};

export default TextArea;
